// dashboardCalculations.js - Score calculations and analytics for TeamDashboard

/**
 * Calculate team scores from response data
 * @param {Array} responsesData - Array of response objects
 * @param {Array} tredes - Array of trede keys (e.g., ['vertrouwen', 'conflicten', ...])
 * @returns {Object} Object with calculated team scores per trede
 */
export const calculateTeamScores = (responsesData, tredes) => {
  if (!responsesData || !Array.isArray(responsesData) || responsesData.length === 0) {
    return {};
  }
  
  if (!tredes || !Array.isArray(tredes)) {
    console.error('Tredes array is required for score calculation');
    return {};
  }
  
  const scores = {};
  
  tredes.forEach(trede => {
    const tredeScores = responsesData
      .map(response => response.tredeScores?.[trede])
      .filter(score => score !== undefined && score !== null && !isNaN(score));
    
    if (tredeScores.length > 0) {
      const average = tredeScores.reduce((sum, score) => sum + score, 0) / tredeScores.length;
      scores[trede] = Math.round(average * 10) / 10; // Round to 1 decimal place
    }
  });

  return scores;
};

/**
 * Determine the focus trede (lowest scoring trede under threshold)
 * @param {Object} teamScores - Team scores object
 * @param {Array} tredes - Array of trede keys in priority order
 * @param {number} threshold - Score threshold for focus determination (default: 3.5)
 * @returns {string|null} The trede key that needs focus, or null if all scores are above threshold
 */
export const determineFocusTrede = (teamScores, tredes, threshold = 3.5) => {
  if (!teamScores || typeof teamScores !== 'object') {
    return null;
  }
  
  if (!tredes || !Array.isArray(tredes)) {
    return null;
  }
  
  // Find the first trede (in order) that scores below threshold
  const focusCandidate = tredes.find(trede => {
    const score = teamScores[trede];
    return score !== undefined && score !== null && score < threshold;
  });
  
  return focusCandidate || null;
};

/**
 * Get color for a score based on performance thresholds
 * @param {number} score - The score to evaluate
 * @param {Object} thresholds - Custom thresholds (optional)
 * @returns {string} Hex color code
 */
export const getScoreColor = (score, thresholds = {}) => {
  if (score === undefined || score === null || isNaN(score)) {
    return '#9ca3af'; // Gray for undefined scores
  }
 
  if (score >= 4.0) return '#22543d'; // Groen
  if (score >= 3.5) return '#d69e2e'; // Geel  
  return '#c53030';                   // Rood
};

/**
 * Get performance label for a score
 * @param {number} score - The score to evaluate
 * @param {Object} thresholds - Custom thresholds (optional)
 * @returns {string} Performance label
 */
export const getScoreLabel = (score, thresholds = {}) => {
  const {
    excellent = 4.0,
    good = 3.5,
    sufficient = 3.0
  } = thresholds;
  
  if (score === undefined || score === null || isNaN(score)) {
    return 'Geen data';
  }
  
  if (score >= excellent) return '🌟 Sterk';
  if (score >= good) return '👍 Voldoende';
  if (score >= sufficient) return '⚠️ Aandacht';
  return '⚠️ Aandacht';
};

/**
 * Get CSS class name for pyramid item based on score
 * @param {number} score - The score to evaluate
 * @param {boolean} isFocus - Whether this trede is the focus area
 * @returns {string} CSS class suffix
 */
export const getPyramidItemClass = (score, isFocus = false) => {
  if (isFocus) return 'attention';
  if (score >= 4.0) return 'strong';
  if (score >= 3.5) return 'sufficient';
  return 'attention';
};

/**
 * Calculate completion statistics
 * @param {number} totalInvited - Total number of invited members
 * @param {number} totalCompleted - Total number of completed responses
 * @returns {Object} Statistics object with rates and counts
 */
export const calculateCompletionStats = (totalInvited, totalCompleted) => {
  const invited = Math.max(0, totalInvited || 0);
  const completed = Math.max(0, totalCompleted || 0);
  const pending = Math.max(0, invited - completed);
  
  const completionRate = invited > 0 ? 
    Math.round((completed / invited) * 100) : 0;
  
  return {
    totalInvited: invited,
    totalCompleted: completed,
    totalPending: pending,
    completionRate,
    isComplete: invited > 0 && completed === invited,
    hasResponses: completed > 0
  };
};

/**
 * Analyze team performance and provide insights
 * @param {Object} teamScores - Team scores object
 * @param {Object} tredeNames - Mapping of trede keys to display names
 * @param {string} focusTrede - The focus trede key
 * @returns {Object} Analysis object with insights and recommendations
 */
export const analyzeTeamPerformance = (teamScores, tredeNames, focusTrede) => {
  if (!teamScores || Object.keys(teamScores).length === 0) {
    return {
      hasData: false,
      message: 'Nog geen data beschikbaar voor analyse'
    };
  }
  
  const scores = Object.values(teamScores).filter(score => 
    score !== undefined && score !== null && !isNaN(score)
  );
  
  if (scores.length === 0) {
    return {
      hasData: false,
      message: 'Geen geldige scores gevonden'
    };
  }
  
  const avgScore = scores.reduce((sum, score) => sum + score, 0) / scores.length;
  const minScore = Math.min(...scores);
  const maxScore = Math.max(...scores);
  
  const strongAreas = Object.entries(teamScores)
    .filter(([_, score]) => score >= 4.0)
    .map(([trede, _]) => tredeNames[trede] || trede);
  
  const improvementAreas = Object.entries(teamScores)
    .filter(([_, score]) => score < 3.5)
    .map(([trede, _]) => tredeNames[trede] || trede);
  
  let overallAssessment = 'Goed';
  if (avgScore >= 4.0) overallAssessment = 'Uitstekend';
  else if (avgScore < 3.5) overallAssessment = 'Verbetering nodig';
  
  const analysis = {
    hasData: true,
    averageScore: Math.round(avgScore * 10) / 10,
    minScore: Math.round(minScore * 10) / 10,
    maxScore: Math.round(maxScore * 10) / 10,
    overallAssessment,
    strongAreas,
    improvementAreas,
    focusArea: focusTrede ? (tredeNames[focusTrede] || focusTrede) : null,
    totalScores: scores.length,
    scoreRange: Math.round((maxScore - minScore) * 10) / 10
  };
  
  return analysis;
};

/**
 * Generate recommendation text based on focus trede and team performance
 * @param {string} focusTrede - The focus trede key
 * @param {Object} tredeNames - Mapping of trede keys to display names
 * @param {Object} teamScores - Team scores object
 * @returns {Object} Recommendation object with type, title, and text
 */
export const generateRecommendation = (focusTrede, tredeNames, teamScores) => {
  if (!focusTrede) {
    // All scores are above threshold
    return {
      type: 'excellent',
      title: 'Uitstekende Team Performance!',
      text: 'Alle tredes scoren 3.5 of hoger! Dit team heeft een sterke basis. Focus nu op het verder versterken van de laagst scorende trede of algemene team ontwikkeling.'
    };
  }
  
  const focusName = tredeNames[focusTrede] || focusTrede;
  const score = teamScores[focusTrede];
  
  return {
    type: 'focus',
    title: 'Prioriteit Aanbeveling',
    text: `${focusName} scoort ${score ? score.toFixed(1) : 'laag'} en heeft de hoogste prioriteit. Begin hier voordat je aan hogere tredes gaat werken. Volgens Lencioni's model vormt dit de basis voor alle andere tredes.`
  };
};

/**
 * Validate response data structure
 * @param {Array} responses - Response data to validate
 * @returns {Object} Validation result with details
 */
export const validateResponseData = (responses) => {
  if (!responses || !Array.isArray(responses)) {
    return {
      isValid: false,
      error: 'Responses must be an array',
      details: 'No valid response data provided'
    };
  }
  
  if (responses.length === 0) {
    return {
      isValid: true,
      isEmpty: true,
      details: 'No responses yet - waiting for team members to complete assessment'
    };
  }
  
  const invalidResponses = responses.filter(response => 
    !response.tredeScores || 
    typeof response.tredeScores !== 'object' ||
    Object.keys(response.tredeScores).length === 0
  );
  
  if (invalidResponses.length > 0) {
    return {
      isValid: false,
      error: 'Invalid response structure',
      details: `${invalidResponses.length} responses missing tredeScores data`,
      validResponses: responses.length - invalidResponses.length,
      invalidResponses: invalidResponses.length
    };
  }
  
  return {
    isValid: true,
    isEmpty: false,
    details: `${responses.length} valid responses found`,
    totalResponses: responses.length
  };
};

/**
 * Get progress bar width percentage with smooth animation
 * @param {number} completionRate - Completion rate (0-100)
 * @param {boolean} animated - Whether to include animation class
 * @returns {Object} Style object for progress bar
 */
export const getProgressBarStyle = (completionRate, animated = true) => {
  const safeRate = Math.max(0, Math.min(100, completionRate || 0));
  
  return {
    width: `${safeRate}%`,
    transition: animated ? 'width 1s ease' : 'none',
    background: safeRate === 100 ? 
      'linear-gradient(90deg, #48bb78 0%, #38a169 100%)' : 
      'linear-gradient(90deg, #48bb78 0%, #38a169 100%)'
  };
};